/* eslint-disable */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce, isEqual, set } from 'lodash';
import moment from 'moment';
import { COLLECTION_SHOP_AUTOMATION_DEFAULTS } from '../../modules/constants';
import { IShopSalesAutomation } from '../../modules/types';

interface IUseSalesAutomationFormStateOptions {
  debounceUpdates?: boolean;
  updateWithInvalidValues?: boolean;
  // `data` is the full config for the optimistic local/redux update; `patch`
  // carries only the fields the user actually changed, for the server update.
  updateCallback?: (data: IShopSalesAutomation, patch: Partial<IShopSalesAutomation>) => void;
}

const NUMERIC_FIELD_PATHS = [
  'couponCodeAdvertising.startInDays',
  'couponCodeAdvertising.endsAfterDays',
  'freeShipping.startsFrom',
  'couponCodeAdvertising.amountOfImagesBetweenBanners',
  'anniversaryCouponCodeAdvertising.amountOfImagesBetweenBanners',
  'galleryExpires.expiresInDays'
];

// Immutable nested set: clones only the objects along `path`, so untouched
// branches keep their reference (lets React.memo'd sections bail out) while the
// changed branch gets a fresh reference.
const setIn = (obj: any, path: string, value: unknown): any => {
  const [head, ...rest] = path.split('.');
  if (rest.length === 0) {
    return { ...obj, [head]: value };
  }
  return { ...obj, [head]: setIn(obj?.[head] ?? {}, rest.join('.'), value) };
};

export const useSalesAutomationFormState = (
  values: IShopSalesAutomation,
  options: IUseSalesAutomationFormStateOptions = {}
) => {
  const { debounceUpdates = true, updateWithInvalidValues = false, updateCallback } = options;

  const [automationState, setAutomationState] = useState<IShopSalesAutomation>(
    COLLECTION_SHOP_AUTOMATION_DEFAULTS
  );
  // Tracked per picker: once a picker's offset is edited locally its stored
  // absolute date is stale, but the other picker's is not — a shared flag made
  // an end-date edit re-derive the start date from startInDays, which is 0 for
  // an already started campaign and so showed "today" (SBK-9496).
  const [couponStartDateChanged, setCouponStartDateChanged] = useState(false);
  const [couponEndDateChanged, setCouponEndDateChanged] = useState(false);

  // Refs keep the handlers below stable (referentially constant across renders)
  // so React.memo on the section components actually prevents re-renders.
  const stateRef = useRef(automationState);
  stateRef.current = automationState;
  const updateCallbackRef = useRef(updateCallback);
  updateCallbackRef.current = updateCallback;

  // Accumulates only the leaves the user touched since the last flush, so the
  // server update carries just those fields instead of the whole config.
  const pendingPatchRef = useRef<Partial<IShopSalesAutomation>>({});

  useEffect(() => {
    if (values && !isEqual(values, automationState)) {
      setAutomationState(values);
    }
  }, [values]);

  const queuePatch = useCallback((fieldPath: string, value: unknown) => {
    set(pendingPatchRef.current, fieldPath, value);
  }, []);

  const flushPatch = useCallback((fullState: IShopSalesAutomation) => {
    if (updateCallbackRef.current) {
      updateCallbackRef.current(fullState, pendingPatchRef.current);
    }
    pendingPatchRef.current = {};
  }, []);

  const debouncedFlush = useMemo(() => debounce(flushPatch, 1000), [flushPatch]);

  const saveValues = debounceUpdates ? debouncedFlush : flushPatch;

  const daysFromToday = useCallback((dateStr: string): number => {
    const date = moment(new Date(dateStr)).startOf('day');
    const today = moment().startOf('day');
    return date.diff(today, 'days');
  }, []);

  const dateFromDays = useCallback((days: number): string => {
    return moment().startOf('day').add(days, 'days').toDate().toString();
  }, []);

  const updateFormValue = useCallback(
    (fieldPath: string, value: unknown) => {
      const current = stateRef.current;
      const isNumValue = NUMERIC_FIELD_PATHS.includes(fieldPath);

      let fieldValue: unknown = isNumValue && value !== '' ? Number(value) : value;

      // Clamp the coupon campaign offsets on the edited field only — no
      // recomputation of any other field happens here.
      if (fieldPath === 'couponCodeAdvertising.endsAfterDays' && (fieldValue as number) < 1) {
        fieldValue = 1;
      }
      if (fieldPath === 'couponCodeAdvertising.startInDays' && (fieldValue as number) < 0) {
        fieldValue = 0;
      }

      let newData = setIn(current, fieldPath, fieldValue) as IShopSalesAutomation;

      // Enabling the coupon ad needs at least a 1-day window.
      let clampedEndsAfterDays: number | undefined;
      if (
        fieldPath === 'couponCodeAdvertising.enabled' &&
        newData.couponCodeAdvertising.endsAfterDays < 1
      ) {
        newData = setIn(newData, 'couponCodeAdvertising.endsAfterDays', 1) as IShopSalesAutomation;
        clampedEndsAfterDays = 1;
      }

      setAutomationState(newData);

      const { couponId: advertisingCouponId } = newData.couponCodeAdvertising || {};
      const { couponId: afterPurchaseCouponId } = newData.couponCodeAfterPurchase || {};
      const { couponId: anniversaryCouponId } = newData.anniversaryCouponCodeAdvertising || {};

      const isOneCouponUsedTwice =
        advertisingCouponId &&
        afterPurchaseCouponId &&
        afterPurchaseCouponId === advertisingCouponId;

      const isOneAnniversaryCouponUsedTwice =
        anniversaryCouponId && afterPurchaseCouponId && afterPurchaseCouponId === anniversaryCouponId;

      const isInvalid = isOneCouponUsedTwice || (isNumValue && value === '');
      const isInvalidAnniversary = isOneAnniversaryCouponUsedTwice || (isNumValue && value === '');

      if (!updateWithInvalidValues && isInvalid) return;

      if (!updateWithInvalidValues && isInvalidAnniversary) return;

      queuePatch(fieldPath, fieldValue);
      if (clampedEndsAfterDays !== undefined) {
        queuePatch('couponCodeAdvertising.endsAfterDays', clampedEndsAfterDays);
      }
      saveValues(newData);
    },
    [queuePatch, saveValues, updateWithInvalidValues]
  );

  const updateStartDate = useCallback(
    (dateStr: string) => {
      if (!dateStr) {
        const prev = stateRef.current;
        const updated = {
          ...prev,
          couponCodeAdvertising: {
            ...prev.couponCodeAdvertising,
            startInDays: 0,
            endsAfterDays: 1
          }
        };
        setAutomationState(updated);
        queuePatch('couponCodeAdvertising.startInDays', 0);
        queuePatch('couponCodeAdvertising.endsAfterDays', 1);
        setCouponStartDateChanged(true);
        setCouponEndDateChanged(true);
        saveValues(updated);
        return;
      }

      const startInDays = daysFromToday(dateStr);
      const prev = stateRef.current;
      const currentEndDateAbsolute =
        prev.couponCodeAdvertising.startInDays + prev.couponCodeAdvertising.endsAfterDays;

      let newEndsAfterDays = currentEndDateAbsolute - startInDays;
      if (newEndsAfterDays < 1) {
        newEndsAfterDays = 1;
      }

      const updated = {
        ...prev,
        couponCodeAdvertising: {
          ...prev.couponCodeAdvertising,
          startInDays,
          endsAfterDays: newEndsAfterDays
        }
      };

      setAutomationState(updated);
      // Moving the start keeps the same end date, so endsAfterDays adjusts too;
      // both offsets go to the server, which recomputes startsAt/endsAt.
      queuePatch('couponCodeAdvertising.startInDays', startInDays);
      queuePatch('couponCodeAdvertising.endsAfterDays', newEndsAfterDays);
      setCouponStartDateChanged(true);
      setCouponEndDateChanged(true);
      saveValues(updated);
    },
    [daysFromToday, queuePatch, saveValues]
  );

  const updateEndDate = useCallback(
    (dateStr: string) => {
      const prev = stateRef.current;

      // The server computes the new end date from the stored start date, so if
      // the whole campaign window is already in the past, re-anchor the start
      // to today (startInDays: 0) — otherwise the recomputed end lands in the
      // past again no matter what the user picked. This applies to clearing
      // the field as well (SBK-9498), not only to picking a new date.
      const { startsAt, endsAt } = prev.couponCodeAdvertising;
      const now = Date.now();
      const isWindowInPast =
        !!startsAt &&
        !!endsAt &&
        new Date(startsAt).getTime() < now &&
        new Date(endsAt).getTime() < now;

      if (!dateStr) {
        const updated = {
          ...prev,
          couponCodeAdvertising: {
            ...prev.couponCodeAdvertising,
            ...(isWindowInPast && { startInDays: 0 }),
            endsAfterDays: 1
          }
        };
        setAutomationState(updated);
        if (isWindowInPast) {
          queuePatch('couponCodeAdvertising.startInDays', 0);
          setCouponStartDateChanged(true);
        }
        queuePatch('couponCodeAdvertising.endsAfterDays', 1);
        setCouponEndDateChanged(true);
        saveValues(updated);
        return;
      }

      const endDateDaysFromToday = daysFromToday(dateStr);

      const startInDays = isWindowInPast ? 0 : prev.couponCodeAdvertising.startInDays;

      let endsAfterDays = endDateDaysFromToday - startInDays;
      if (endsAfterDays < 1) {
        endsAfterDays = 1;
      }

      const updated = {
        ...prev,
        couponCodeAdvertising: {
          ...prev.couponCodeAdvertising,
          ...(isWindowInPast && { startInDays: 0 }),
          endsAfterDays
        }
      };

      setAutomationState(updated);
      if (isWindowInPast) {
        queuePatch('couponCodeAdvertising.startInDays', 0);
        setCouponStartDateChanged(true);
      }
      // Only the duration changed (plus the start re-anchor above for a past
      // window); the server keeps the stored start date otherwise, so the start
      // picker must keep showing the stored startsAt.
      queuePatch('couponCodeAdvertising.endsAfterDays', endsAfterDays);
      setCouponEndDateChanged(true);
      saveValues(updated);
    },
    [daysFromToday, queuePatch, saveValues]
  );

  const updateAnniversaryDate = useCallback(
    (dateStr: string) => {
      if (!dateStr) {
        return;
      }

      const selectedDate = new Date(dateStr);
      if (isNaN(selectedDate.getTime())) {
        return;
      }

      const prev = stateRef.current;
      const prevIso =
        prev.anniversaryCouponCodeAdvertising?.anniversaryDate ?? new Date().toISOString();
      const prevDate = new Date(prevIso);

      const updatedDate = new Date(prevDate);
      updatedDate.setMonth(selectedDate.getMonth(), selectedDate.getDate());

      const anniversaryDate = updatedDate.toISOString();

      const updated = {
        ...prev,
        anniversaryCouponCodeAdvertising: {
          ...prev.anniversaryCouponCodeAdvertising,
          anniversaryDate
        }
      };

      setAutomationState(updated);

      if (prevIso !== dateStr) {
        // Only the anniversary date changed — the coupon campaign window is
        // left untouched.
        queuePatch('anniversaryCouponCodeAdvertising.anniversaryDate', anniversaryDate);
        saveValues(updated);
      }
    },
    [queuePatch, saveValues]
  );

  return {
    automationState,
    couponStartDateChanged,
    couponEndDateChanged,
    updateFormValue,
    updateStartDate,
    updateEndDate,
    updateAnniversaryDate,
    daysFromToday,
    dateFromDays
  };
};
